import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock } from '@fortawesome/free-solid-svg-icons';
import { requestPasswordReset, resetPassword } from '../../api';
import './ResetPassword.css';

const ResetPassword = () => {
  const screenHeight = window.innerHeight;
  const navbarHeight = 80;
  const navigate = useNavigate();

  const [step, setStep] = useState(1); // 1 = request code, 2 = enter code + new password
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  const {
    register: registerStep1,
    handleSubmit: handleStep1,
    formState: { errors: errors1 },
  } = useForm();

  const {
    register: registerStep2,
    handleSubmit: handleStep2,
    watch,
    formState: { errors: errors2 },
  } = useForm();

  const onRequestCode = async (data) => {
    setError(null);
    setLoading(true);
    try {
      await requestPasswordReset(data.email);
    } catch {
      // Intentionally ignored — the backend returns the same response
      // regardless of whether the email exists, to avoid leaking that info.
    } finally {
      setLoading(false);
    }
    // Always advance to step 2 with a neutral message
    setEmail(data.email);
    setSuccessMessage('Si tu correo está registrado, recibirás un código de 6 dígitos en breve.');
    setStep(2);
  };

  const onResetPassword = async (data) => {
    setError(null);
    setLoading(true);
    try {
      await resetPassword({ email, code: data.code, password: data.password });
      navigate('/login');
    } catch (err) {
      // Backend returns "Código de verificación incorrecto" for both wrong code
      // and non-existent email, intentionally to avoid leaking email existence.
      setError(err.response?.data?.message || 'Código de verificación incorrecto');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className='flex font-montserrat font-semibold w-full'
      style={{ height: `${screenHeight - navbarHeight}px` }}
    >
      <div className='hidden md:flex md:max-w-[25%] lg:max-w-[50%] w-full login-image shadow-inner' />
      <div className='flex justify-center max-w-full md:max-w-[75%] lg:max-w-[50%] w-full mx-5'>
        <div className='bg-white shadow-xl w-full my-auto p-4 px-10 max-w-[500px] xl:max-w-[600px]'>
          <div className='flex justify-center my-5'>
            <div className='flex items-center justify-center w-16 h-16 rounded-full bg-red-50 text-red-1'>
              <FontAwesomeIcon icon={faLock} className='text-2xl' />
            </div>
          </div>

          <h1 className='text-center text-2xl'>Restablecer contraseña</h1>

          {/* Step indicators */}
          <div className='flex items-center gap-2 my-5'>
            <div className={`flex-1 h-1 rounded-full transition-colors ${step >= 1 ? 'bg-red-1' : 'bg-gray-200'}`} />
            <div className={`flex-1 h-1 rounded-full transition-colors ${step >= 2 ? 'bg-red-1' : 'bg-gray-200'}`} />
          </div>

          {/* Step 1 — Request code */}
          {step === 1 && (
            <form className='flex flex-col' onSubmit={handleStep1(onRequestCode)}>
              <p className='text-center text-gray-500 text-sm mb-4'>
                Ingresa tu correo y te enviaremos un código para restablecer tu contraseña.
              </p>

              <label htmlFor='email' className='text-red-1 mt-2'>
                Correo electrónico
              </label>
              <input
                id='email'
                className='login-input w-full h-9 outline-none focus:border-red-2 transition-colors pl-2 text-gray-600'
                type='email'
                {...registerStep1('email', { required: true })}
              />
              {errors1.email && <span className='text-sm text-red-600 mt-1'>Este campo es requerido</span>}

              {error && (
                <p className='mt-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2 text-center'>
                  {error}
                </p>
              )}

              <button
                type='submit'
                disabled={loading}
                className='mt-6 bg-red-1 text-white font-semibold py-2 rounded hover:bg-red-2 disabled:opacity-60 disabled:cursor-not-allowed transition-colors'
              >
                {loading ? 'Enviando...' : 'Enviar código'}
              </button>
              <hr className='my-3' />
              <Link
                to='/login'
                className='text-center text-sm text-red-1 hover:underline'
              >
                Volver al inicio de sesión
              </Link>
            </form>
          )}

          {/* Step 2 — Enter code + new password */}
          {step === 2 && (
            <form className='flex flex-col' onSubmit={handleStep2(onResetPassword)}>
              {successMessage && (
                <p className='mb-4 text-sm text-green-700 bg-green-50 border border-green-200 rounded px-3 py-2 text-center'>
                  {successMessage}
                </p>
              )}

              <label htmlFor='code' className='text-red-1 mt-2'>
                Código de verificación
              </label>
              <input
                id='code'
                className='login-input w-full h-9 outline-none focus:border-red-2 transition-colors pl-2 text-gray-600 tracking-widest'
                type='text'
                placeholder='Código de 6 dígitos'
                maxLength={6}
                {...registerStep2('code', { required: true })}
              />
              {errors2.code && <span className='text-sm text-red-600 mt-1'>Este campo es requerido</span>}

              <label htmlFor='password' className='text-red-1 mt-5'>
                Nueva contraseña
              </label>
              <input
                id='password'
                className='login-input w-full h-9 outline-none focus:border-red-2 transition-colors pl-2 text-gray-600'
                type='password'
                {...registerStep2('password', { required: true, minLength: 6 })}
              />
              {errors2.password && (
                <span className='text-sm text-red-600 mt-1'>
                  {errors2.password.type === 'minLength' ? 'Mínimo 6 caracteres' : 'Este campo es requerido'}
                </span>
              )}

              <label htmlFor='confirmPassword' className='text-red-1 mt-5'>
                Confirmar contraseña
              </label>
              <input
                id='confirmPassword'
                className='login-input w-full h-9 outline-none focus:border-red-2 transition-colors pl-2 text-gray-600'
                type='password'
                {...registerStep2('confirmPassword', {
                  required: true,
                  validate: (v) => v === watch('password') || 'Las contraseñas no coinciden',
                })}
              />
              {errors2.confirmPassword && (
                <span className='text-sm text-red-600 mt-1'>{errors2.confirmPassword.message || 'Este campo es requerido'}</span>
              )}

              {error && (
                <p className='mt-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2 text-center'>
                  {error}
                </p>
              )}

              <button
                type='submit'
                disabled={loading}
                className='mt-6 bg-red-1 text-white font-semibold py-2 rounded hover:bg-red-2 disabled:opacity-60 disabled:cursor-not-allowed transition-colors'
              >
                {loading ? 'Guardando...' : 'Restablecer contraseña'}
              </button>
              <hr className='my-3' />
              <button
                type='button'
                onClick={() => { setStep(1); setError(null); setSuccessMessage(null); }}
                className='text-center text-sm text-red-1 hover:underline'
              >
                ¿No recibiste el código? Volver a enviar
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
